import {
  useCallback,
  useMemo,
  useState,
  type MouseEvent,
  type RefObject,
} from 'react';
import { containsUrl } from '@/components/LinkifiedText';

// Both caret APIs are optional here: they appear in the TS lib but are absent in
// jsdom (test env) and caretRangeFromPoint is also absent in non-WebKit browsers.
type DocWithCaretApis = Omit<
  Document,
  'caretPositionFromPoint' | 'caretRangeFromPoint'
> & {
  caretPositionFromPoint?: Document['caretPositionFromPoint'];
  caretRangeFromPoint?: Document['caretRangeFromPoint'];
};

const docWithCaret: DocWithCaretApis = document;

/**
 * Returns the character offset within `container`'s text content at the given
 * coordinates, using whichever caret API the browser provides:
 * - `caretPositionFromPoint` (W3C standard — Chrome 128+, Firefox)
 * - `caretRangeFromPoint` (WebKit legacy — Safari)
 *
 * Returns `null` when neither API is available (e.g. jsdom) or the resolved
 * node falls outside `container`.
 *
 * Uses `Range.toString()` to count characters, which correctly handles both
 * text-node offsets (character index) and element-node offsets (child index)
 * that browsers sometimes return at element boundaries.
 */
function getCaretOffsetFromPoint(
  x: number,
  y: number,
  container: Element,
): number | null {
  const stdCaret = docWithCaret.caretPositionFromPoint?.(x, y);
  let caretNode: Node | null = stdCaret?.offsetNode ?? null;
  let caretNodeOffset = stdCaret?.offset ?? 0;

  if (!stdCaret) {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const range = docWithCaret.caretRangeFromPoint?.(x, y);
    if (range) {
      caretNode = range.startContainer;
      caretNodeOffset = range.startOffset;
    }
  }

  if (caretNode === null || !container.contains(caretNode)) {
    return null;
  }

  // Build a range from the start of `container` to the caret position, then
  // measure its text length. This handles both text-node offsets (character
  // index) and element-node offsets (child index) uniformly.
  try {
    const range = document.createRange();
    range.setStart(container, 0);
    range.setEnd(caretNode, caretNodeOffset);
    return range.toString().length;
  } catch {
    return null;
  }
}

interface UseUrlOverlayResult {
  isFocused: boolean;
  hasUrls: boolean;
  handleFocus: () => void;
  handleBlur: () => void;
  handleOverlayClick: (e: MouseEvent<HTMLDivElement>) => void;
}

export function useUrlOverlay(
  title: string,
  textareaRef: RefObject<HTMLTextAreaElement | null>,
): UseUrlOverlayResult {
  const [isFocused, setIsFocused] = useState(false);
  const hasUrls = useMemo(() => containsUrl(title), [title]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const handleOverlayClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target instanceof Element && e.target.closest('a')) {
        return;
      }
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }
      const charOffset =
        getCaretOffsetFromPoint(e.clientX, e.clientY, e.currentTarget) ??
        textarea.value.length;
      textarea.focus();
      textarea.setSelectionRange(charOffset, charOffset);
    },
    [textareaRef],
  );

  return { isFocused, hasUrls, handleFocus, handleBlur, handleOverlayClick };
}
